import React, { useState } from 'react';
import PrioritySliders from './PrioritySliders';
import './Survey.css';

interface SurveyProps {
  onComplete: (data: any) => void;
  onClose: () => void;
}

const Survey: React.FC<SurveyProps> = ({ onComplete, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [surveyData, setSurveyData] = useState({
    projectType: '',
    timeline: '',
    budget: '',
    priorities: { speed: 33, budget: 33, quality: 33 },
    experience: '',
    team: ''
  });

  const questions = [
    {
      question: "What type of project are you looking to build?",
      type: "choice",
      options: [
        "Mobile App (iOS/Android)",
        "Web Application",
        "Desktop Software",
        "AI/ML Integration",
        "Not sure yet"
      ],
      key: "projectType"
    },
    {
      question: "What's your ideal timeline?",
      type: "choice",
      options: [
        "ASAP (Rush job)",
        "1-3 months",
        "3-6 months", 
        "6+ months",
        "Flexible"
      ],
      key: "timeline"
    },
    {
      question: "What's your budget range?",
      type: "choice",
      options: [
        "Under $10k",
        "$10k - $50k",
        "$50k - $100k",
        "$100k+",
        "Let's discuss"
      ],
      key: "budget"
    },
    {
      question: "Set your project priorities",
      type: "triangle",
      key: "priorities"
    },
    {
      question: "Do you have experience with app development?",
      type: "choice",
      options: [
        "This is my first app",
        "I've built apps before",
        "I'm a developer myself", 
        "I work at a tech company",
        "I'm an entrepreneur"
      ],
      key: "experience"
    },
    {
      question: "Do you have a team already?",
      type: "choice",
      options: [
        "Just me",
        "Small team (2-5 people)",
        "Medium team (5-20 people)",
        "Large team (20+ people)",
        "Looking to build a team"
      ],
      key: "team"
    }
  ];

  const handleAnswer = (value: any) => {
    setSurveyData(prev => ({
      ...prev,
      [questions[currentStep].key]: value
    }));

    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete({
        ...surveyData,
        [questions[currentStep].key]: value
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <div className="survey-overlay">
      <div className="survey-modal">
        <div className="survey-header">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="survey-content">
          <div className="question-counter">
            Question {currentStep + 1} of {questions.length}
          </div>
          
          <h2 className="question-text">{currentQuestion.question}</h2>

          {currentQuestion.type === "choice" && (
            <div className="options-container">
              {currentQuestion.options?.map((option, index) => (
                <button
                  key={index}
                  className="option-button"
                  onClick={() => handleAnswer(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {currentQuestion.type === "triangle" && (
            <div className="triangle-container">
              <PrioritySliders
                onChange={(values) => {
                  setSurveyData(prev => ({
                    ...prev,
                    priorities: values
                  }));
                }}
                initialValues={surveyData.priorities}
              />
              <button
                className="continue-button"
                onClick={() => handleAnswer(surveyData.priorities)}
              >
                Continue
              </button>
            </div>
          )}
        </div>

        <div className="survey-footer">
          {currentStep > 0 && (
            <button className="back-button" onClick={handleBack}>
              ← Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Survey;